<script setup>
import {computed, onMounted, ref} from "vue";
import axios from "axios";
import {Steps} from "@/composables/ai-onboarding/useSteps";
import CoverageStep from "@/components/ai-onboarding/steps/CoverageStep.vue";
import BusinessDescriptionStep from "@/components/ai-onboarding/steps/BusinessDescriptionStep.vue";
import ServicesOfferedStep from "@/components/ai-onboarding/steps/ServicesOfferedStep.vue";
import CompetitorsStep from "@/components/ai-onboarding/steps/CompetitorsStep.vue";
import OnboardingStep from "@/components/ai-onboarding/steps/OnboardingStep.vue";
import ProgressBar from "@/components/ai-onboarding/partials/ProgressBar.vue";
import HelpButton from "@/components/global-themed/buttons/HelpButton.vue";
import FadeTransition from "@/components/transitions/FadeTransition.vue";
import GoogleAccountsStep from "@/components/ai-onboarding/steps/GoogleAccountsStep.vue";
import LoadingBlock from "@/components/global-themed/LoadingBlock.vue";
import HoldingStep from "@/components/ai-onboarding/steps/HoldingStep.vue";
import KeywordsStep from "@/components/ai-onboarding/steps/KeywordsStep.vue";
import PagesStep from "@/components/ai-onboarding/steps/PagesStep.vue";
import AIAutoAssignHolding from "@/components/ai-onboarding/steps/AIAutoAssignHolding.vue";
import OptimizerStep from "@/components/ai-onboarding/steps/OptimizerStep.vue";
import KitIcon from "@/components/approval-center/partials/kit/KitIcon.vue";
import SummaryModal from "@/components/ai-onboarding/modals/SummaryModal.vue";
import KitOnboardingStep from "@/components/ai-onboarding/steps/KitOnboardingStep.vue";
import useKit from "@/composables/useKit";
import useWebsite from "@/composables/useWebsite";
import useUser from "@/composables/useUser";
import useCompleteOnboarding from "@/composables/ai-onboarding/useCompleteOnboarding";

defineProps({
  location: {
    type: Object,
    required: true,
  },
  canBypassAiOnboarding: {
    type: Boolean,
    required: true,
  },
});

const currentStep = ref(Steps.ONBOARDING.key);
const loading = ref(true);

onMounted(async () => {
  await redirectToCorrectStep();
});

const {canSeeKit, isUserLoading} = useKit();
const {mutate: completeOnboarding} = useCompleteOnboarding().completeOnboardingMutation;

const redirectToCorrectStep = async () => {
  const { data } = await axios.get('/ajax/v2/ai-onboarding/current-step');

  const possibleStep = Object.values(Steps).find(progressStep => progressStep.key === data.data.currentStep)?.key;

  if (!possibleStep) {
    return;
  }

  currentStep.value = possibleStep;
  loading.value = false;
}

const handleStepChange = (nextStep) => {
  // If user should skip optimizer and next step is optimizer, skip to completion
  if (shouldSkipOptimizer.value && nextStep.key === Steps.ONSITE_OPTIMIZER.key) {
    completeOnboarding();
    return;
  }

  if (Object.values(Steps).some(step => step.key === nextStep.key)) {
    currentStep.value = nextStep.key;
    return;
  }

  throw new Error(`Invalid step: ${nextStep.key}`);
};

const { activeWebsiteIsFreeAppsumo } = useWebsite();
const { hasAccessToOptimiser } = useUser();

const shouldSkipOptimizer = computed(() => {
  return activeWebsiteIsFreeAppsumo.value || !hasAccessToOptimiser.value;
});

const step = computed(() => Object.values(Steps).find(step => step.key === currentStep.value));

const stepsThatShowKitsSummary = [
  Steps.KEYWORDS.key,
  Steps.PAGES.key
];
</script>

<template>
  <LoadingBlock v-if="loading" />

  <div v-else>
    <FadeTransition>
      <template v-if="currentStep === Steps.ONBOARDING.key">
        <LoadingBlock v-if="isUserLoading" />
        <KitOnboardingStep
          v-else-if="canSeeKit"
          :can-bypass-ai-onboarding="canBypassAiOnboarding"
          @change-step="handleStepChange"
        />

        <OnboardingStep
          v-else
          :can-bypass-ai-onboarding="canBypassAiOnboarding"
          @change-step="handleStepChange"
        />
      </template>
      <div
        v-else
        class="tw-flex tw-flex-col tw-max-w-default-container tw-mx-auto"
      >
        <div class="tw-flex tw-justify-between tw-py-5 tw-items-center">
          <div class="tw-flex tw-gap-4 tw-items-center">
            <KitIcon v-if="canSeeKit" />
            <h1 class="tw-text-grey-dark tw-font-bold tw-text-4.5xl">
              {{ step.heading }}
            </h1>
          </div>

          <SummaryModal
            v-if="stepsThatShowKitsSummary.includes(currentStep)"
            :step="step"
          />

          <HelpButton
            v-if="step.helpUrl"
            :video-url="step.helpUrl"
          />
        </div>

        <div class="tw-flex tw-justify-center tw-items-start tw-space-x-8 tw-h-[calc(100vh-191px)] tw-max-h-[calc(1080px-191px)]">
          <ProgressBar :current-step="currentStep" />

          <div
            class="tw-flex tw-flex-col tw-bg-white tw-w-full tw-rounded-xl tw-justify-between tw-h-full"
          >
            <FadeTransition>
              <CoverageStep
                v-if="currentStep === Steps.LOCATIONS.key"
                :location="location"
                @change-step="handleStepChange"
              />

              <BusinessDescriptionStep
                v-else-if="currentStep === Steps.BUSINESS_DESCRIPTION.key"
                @change-step="handleStepChange"
              />

              <CompetitorsStep
                v-else-if="currentStep === Steps.COMPETITORS.key"
                @change-step="handleStepChange"
              />

              <ServicesOfferedStep
                v-else-if="currentStep === Steps.SERVICES_OFFERED.key"
                @change-step="handleStepChange"
              />

              <GoogleAccountsStep
                v-else-if="currentStep === Steps.ACCOUNTS.key"
                @change-step="handleStepChange"
              />

              <HoldingStep
                v-else-if="currentStep === Steps.HOLDING.key"
                @change-step="handleStepChange"
              />

              <KeywordsStep
                v-else-if="currentStep === Steps.KEYWORDS.key"
                @change-step="handleStepChange"
              />

              <AIAutoAssignHolding
                v-else-if="currentStep === Steps.AI_AUTO_ASSIGN_HOLDING.key"
                @change-step="handleStepChange"
              />

              <PagesStep
                v-else-if="currentStep === Steps.PAGES.key"
                @change-step="handleStepChange"
              />

              <OptimizerStep
                v-else-if="currentStep === Steps.ONSITE_OPTIMIZER.key && !shouldSkipOptimizer"
                @change-step="handleStepChange"
              />
            </FadeTransition>
          </div>
        </div>
      </div>
    </FadeTransition>
  </div>
</template>
